import React, { Suspense, lazy, useState, useEffect } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { ConfigProvider } from "antd";
import frFR from "antd/locale/fr_FR";
import Main from "./components/layout/Main";
import { useSupabaseUser } from "./hooks/fetch";
import { useWebPush } from "./hooks/useWebPush";
import BatilinkLanding from "./website/BatilinkLanding";
import SearchResults from "./website/SearchResults";
import AboutUs from "./website/AboutUs";
import "antd/dist/reset.css";
import "./assets/styles/main.css";
import "./assets/styles/responsive.css";
import Users from './pages/Users';


// Lazy import des pages
const Home = lazy(() => import("./pages/Home"));
const Sales = lazy(() => import("./pages/Sales"));
const Facturation = lazy(() => import("./pages/Facturation"));
const Promos = lazy(() => import("./pages/Promos"));
const Publications = lazy(() => import("./pages/Publications"));
const Clients = lazy(() => import("./pages/Clients"));
const SignIn = lazy(() => import("./pages/SignIn"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const ConfirmEmail = lazy(() => import("./pages/ConfirmEmail"));
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite"));
const Executeur = lazy(() => import("./pages/Executeur"));
const Sponsors = lazy(() => import("./pages/Sponsors"));
const User = lazy(() => import("./pages/user"));
const Supplier = lazy(() => import("./pages/Supplier"));
const Concepteur = lazy(() => import("./pages/Concepteur"));
const Settings = lazy(() => import("./pages/Settings"));
const PaymentHistory = lazy(() => import("./pages/PaymentHistory"));
const PartnerClients = lazy(() => import("./pages/PartnerClients"));
const Reviews = lazy(() => import("./pages/Reclamations"));
const ReviewsListPage = lazy(() => import("./pages/ReviewsListPage"));
const SubAdminReviewsListPage = lazy(() => import("./pages/SubAdminReviewsListPage"));
const UserReviewsDetailPage = lazy(() => import("./pages/UserReviewsDetailPage"));
const Revenu = lazy(() => import("./pages/adminIncomes"));
const AdminIncomeContributors = lazy(() => import("./pages/AdminIncomeUsers"));
const Partenaires = lazy(() => import("./pages/partenaires"));
const Visitor = lazy(() => import("./pages/AdminUsers"));
const SubAdminDashboard = lazy(() => import("./pages/SubAdminDashboard"));
const SubAdminPayments = lazy(() => import("./pages/SubAdminPayments"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Banners = lazy(() => import("./pages/Banners"));
const PartnerRequests = lazy(() => import("./pages/PartnerRequests"));

const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: '#fafafa'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid #f0f0f0',
        borderTop: '4px solid #fdd119',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto'
      }} />
    </div>
  </div>
);

// Lecture sécurisée du rôle depuis localStorage
// — placée dans un hook pour être réactive aux changements de storage
function useAdminRole() {
  const [role, setRole] = useState(() => {
    try {
      const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
      return (adminUser.role || "").toLowerCase();
    } catch {
      // JSON corrompu : on traite comme non connecté
      localStorage.removeItem("adminUser");
      return "";
    }
  });

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "adminUser") {
        try {
          const adminUser = JSON.parse(e.newValue || "{}");
          setRole((adminUser.role || "").toLowerCase());
        } catch {
          setRole("");
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  return role;
}

function App() {
  const role = useAdminRole();

  // Enregistre le navigateur de l'admin connecté pour les Web Push.
  // No-op tant qu'il n'y a pas de session (pages publiques).
  const { user } = useSupabaseUser();
  useWebPush(user?.id || null);

  return (
    <ConfigProvider locale={frFR} modal={{ closable: false }}>
      <div className="App">
        <Suspense fallback={<LoadingFallback />}>
          <Switch>
            {/* Routes publiques sans layout */}
            <Route path="/" exact component={BatilinkLanding} />
            <Route path="/recherche" exact component={SearchResults} />
            <Route path="/about-us" exact component={AboutUs} />
            <Route path="/sign-in" exact component={SignIn} />
            <Route path="/forgot-password" exact component={ForgotPassword} />
            <Route path="/resetpassword" exact component={ResetPassword} />
            <Route path="/set-password" exact component={SetPassword} />
            <Route path="/confirm-email" exact component={ConfirmEmail} />
            <Route path="/politique-confidentialite" exact component={PolitiqueConfidentialite} />

            {/* Routes protégées avec layout Main */}
            <Route path="*">
              <Main>
                <Suspense fallback={null}>
                  {role === "super_admin" ? (
                    <Switch>
                      <Route exact path="/Dashboard" component={Home} />
                      <Route exact path="/Paiement" component={Sales} />
                      <Route exact path="/Promos" component={Promos} />
                      <Route exact path="/Publications" component={Publications} />
                      <Route exact path="/Catégories" component={Clients} />
                      <Route exact path="/Sponsors" component={Sponsors} />
                      <Route exact path="/supplier" component={Supplier} />
                      <Route exact path="/concepteur" component={Concepteur} />
                      <Route exact path="/Utilisateurs" component={Users} />
                      <Route path="/Liste des utilisateurs" component={Visitor} />
                      <Route exact path="/Paramétres" component={Settings} />
                      <Route exact path="/executeur" component={Executeur} />
                      <Route exact path="/Réclamations" component={Reviews} />
                      <Route exact path="/reviews-list" component={ReviewsListPage} />
                      <Route exact path="/user-reviews/:userId" component={UserReviewsDetailPage} />
                      <Route exact path="/Demandes" component={PartnerRequests} />
                      <Route exact path="/Partenaires" component={Partenaires} />
                      <Route exact path="/Facturation" component={Facturation} />
                      <Route exact path="/Clients" component={PartnerClients} />
                      <Route exact path="/Historique de paiement" component={PaymentHistory} />
                      <Route exact path="/Revenu" component={Revenu} />
                      <Route exact path="/paiement/Client" component={AdminIncomeContributors} />
                      <Route exact path="/Notifications" component={Notifications} />
                      <Route exact path="/Banners" component={Banners} />
                      {/* Fallback : redirige vers /Dashboard pour les URL non reconnues */}
                      <Route path="*"><Redirect to="/Dashboard" /></Route>
                    </Switch>
                  ) : role === "sous admin" ? (
                    <Switch>
                      <Route exact path="/Dashboard" component={SubAdminDashboard} />
                      <Route exact path="/Sponsors" component={Sponsors} />
                      <Route exact path="/Clients" component={PartnerClients} />
                      <Route exact path="/Réclamations" component={Reviews} />
                      <Route exact path="/reviews-list" component={ReviewsListPage} />
                      <Route exact path="/reviews-list-partner" component={SubAdminReviewsListPage} />
                      <Route exact path="/user-reviews/:userId" component={UserReviewsDetailPage} />
                      <Route exact path="/Paramétres" component={Settings} />
                      <Route path="/user/:userId" component={User} />
                      <Route exact path="/Facturation" component={Facturation} />
                      <Route exact path="/Revenu" component={Revenu} />
                      <Route exact path="/paiement/Client" component={AdminIncomeContributors} />
                      <Route exact path="/Paiements" component={SubAdminPayments} />
                      <Route exact path="/Notifications" component={Notifications} />
                      {/* Fallback sous_admin : URL inconnue ou non autorisée → redirige vers /Dashboard */}
                      <Route path="*"><Redirect to="/Dashboard" /></Route>
                    </Switch>
                  ) : (
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                      <h3>Accès non autorisé</h3>
                      <p>Veuillez vous connecter pour accéder à cette page.</p>
                    </div>
                  )}
                </Suspense>
              </Main>
            </Route>
          </Switch>
        </Suspense>
      </div>
    </ConfigProvider>
  );
}

export default App;